import React, { useState } from 'react';
import {
  Box, Button, TextField, Typography
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ForgotPasswordForm from './ForgotPasswordForm';
const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:7071';

const LandingPage = ({ onLoginSuccess }) => {
  const [isSignup, setIsSignup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', email: '', phone: '', propertyName: '', propertyEmail: '', propertyAddress: '' });
  const [loginError, setLoginError] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isSignup) {
      if (!form.username || !form.password || !form.propertyName) {
        alert('Please fill in all required fields.');
        return;
      }
      const loginPayload = {
        username: form.username,
        password: form.password,
        propertyName: form.propertyName
      };
      const res = await fetch(`${apiURL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginPayload)
      });

      const data = await res.json();
      if (res.ok) {
        console.log('✅ Login success:', data);
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken || '');
        const role = data.user?.properties?.[0]?.role || 'staff';
        const propertyId = data.user?.propertyId?.id || data.user?.properties?.[0]?.propertyId?.id;
        const permissionRes = await fetch(`${apiURL}/api/role-permissions/${role}?propertyId=${propertyId}`);
        const permissionData = await permissionRes.json();
        const allowedTabs = permissionData?.allowedTabs || [];

        const userWithRole = { ...data.user, role };
        localStorage.setItem('user', JSON.stringify(userWithRole));
        localStorage.setItem('allowedTabs', JSON.stringify(allowedTabs));
        onLoginSuccess();
      } else {
        if (data.error === 'User not found') {
          setLoginError({ username: 'Username not found', password: '' });
        } else if (data.error === 'Invalid password') {
          setLoginError({ username: '', password: 'Password is incorrect' });
        } else {
          alert(data.error || 'Login failed');
        }
      }
      return;
    }

    const payload = {
      username: form.username,
      email: form.email,
      password: form.password,
      phone: form.phone,
      propertyName: form.propertyName,
      propertyEmail: form.propertyEmail,
      propertyAddress: form.propertyAddress
    };

    const checkRes = await fetch(`${apiURL}/api/auth/check-username?username=${form.username}`);
    const checkData = await checkRes.json();
    if (!checkRes.ok || checkData.exists) {
      toast.warn('Username already exists. Please choose a different one.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    try {
      const res = await fetch(`${apiURL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
  
      const data = await res.json();
      if (res.ok) {
        console.log('✅ Signup success:', data);
        setIsSignup(false); // Switch back to login form after successful signup
        toast.success('Signup successful! Please log in.', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
      } else {
        alert(data.error || 'Signup failed');
      }
    } catch (err) {
      console.error('❌ Signup error:', err);
      alert('Signup failed');
    }
  };

  return (
  <Box display="flex" justifyContent="center" alignItems="start" height="100vh" overflow="auto" py={4}>
      <Box p={3} boxShadow={2} borderRadius={1} bgcolor="#fff" width="100%" maxWidth={400}>
        {showForgotPassword ? (
          <ForgotPasswordForm apiURL={apiURL} onClose={() => setShowForgotPassword(false)} />
        ) : (
          <>
            <Typography variant="h5" align="center" mb={2}>
              {isSignup ? 'Sign Up & Register Property' : 'Login'}
            </Typography>
            
            <Typography variant="subtitle1" sx={{ mb: 1.5 }} align="center">User Information</Typography>
            <TextField fullWidth margin="normal" label="Property Name" name="propertyName" value={form.propertyName} onChange={handleChange} sx={{ mb: 1.5 }} />
            <TextField
              fullWidth
              margin="normal"
              label="Username"
              name="username"
              value={form.username}
              onChange={handleChange}
              error={!!loginError.username}
              helperText={loginError.username}
              sx={{ mb: 1.5 }}
            />
            {isSignup && (
              <>
                <TextField fullWidth margin="normal" label="Email" name="email" value={form.email} onChange={handleChange} sx={{ mb: 1.5 }} />
                <TextField fullWidth margin="normal" label="Phone Number" name="phone" value={form.phone} onChange={handleChange} sx={{ mb: 1.5 }} />
              </>
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              error={!!loginError.password}
              helperText={loginError.password}
              sx={{ mb: 1.5 }}
              InputProps={{
                endAdornment: (
                  <Box
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ cursor: 'pointer', color: '#1976d2', display: 'flex', alignItems: 'center' }}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" fill="currentColor">
                     <path d="M19.91 14.255C20.15 14.065 20.37 13.845 20.59 13.615L21.35 12.845C21.55 12.645 21.55 12.325 21.35 12.125L20.59 11.355C17.52 8.265 13.22 7.165 9.26999 8.085H9.26001" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M4.09 10.735C3.85 10.925 3.63001 11.145 3.41001 11.375L2.65 12.145C2.45 12.345 2.45 12.665 2.65 12.865L3.41001 13.635C6.48001 16.725 10.78 17.825 14.73 16.905H14.74" stroke="currentColor"  stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M11.46 14.995C10.93 14.885 10.46 14.605 10.11 14.215" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M13.9001 10.775C13.5501 10.385 13.08 10.105 12.55 9.995" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M3.59998 7.63501L20.41 17.365" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round"></path> 
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 
                                 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 
                                 5zm0-8c-1.65 0-3 1.35-3 3s1.35 3 3 3 3-1.35 3-3-1.35-3-3-3z"/>
                      
                      </svg>
                    )}
                  </Box>
                )
              }}
            />
            {!isSignup && (
              <Typography
                variant="body2"
                align="right"
                sx={{ mt: -1, mb: 2, cursor: 'pointer', color: '#1976d2' }}
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </Typography>
            )}
            {isSignup && (
              <>
                <Typography variant="subtitle1" align="center" sx={{ mb: 1.5 }}>Property Information</Typography>
                <TextField fullWidth margin="normal" label="Property Name" name="propertyName" value={form.propertyName} onChange={handleChange} sx={{ mb: 1.5 }} />
                <TextField fullWidth margin="normal" label="Property Email" name="propertyEmail" value={form.propertyEmail} onChange={handleChange} sx={{ mb: 1.5 }} />
                <TextField fullWidth margin="normal" label="Property Address" name="propertyAddress" value={form.propertyAddress} onChange={handleChange} sx={{ mb: 1.5 }} />
              </>
            )}
            <Button fullWidth variant="contained" color="primary" onClick={handleSubmit}>
              {isSignup ? 'Sign Up' : 'Login'}
            </Button>
            <Button fullWidth onClick={() => setIsSignup(!isSignup)} sx={{ mt: 1 }}>
              {isSignup ? 'Already have an account? Log in' : 'No account? Sign up'}
            </Button>
          </>
        )}
        <ToastContainer />
      </Box>
    </Box>
  );
};

export default LandingPage;
